'use client';

import React, { useId } from 'react';
import NextImage from 'next/image';
import IconComponent from 'ui/src/components/Icon';
import Image from 'ui/src/components/Image';
import twMerge from 'utilities/src/twMerge';
import { widgets } from 'data/widgets';

const { title, description, talents, links } = widgets.hiringEngineers;

/**
 * Talent placement inside the card. Staggered in the sidebar and on phones, stacked to the left in the
 * 640–833px two-half layout, where the third talent is dropped.
 */
const talentClasses = ['self-end mdS:self-start md:self-end', 'self-start', 'self-end mr-4 mdS:hidden md:block'];

type Props = {
  className?: string;
};

/**
 * "Hiring engineers?" conversion card (static content).
 */
export default function HiringCard({ className }: Props) {
  const titleId = useId();

  return (
    <section
      aria-labelledby={titleId}
      className={twMerge(
        'border-site-accent-200 bg-site-neutrals-900 shadow-lv5 flex flex-col overflow-hidden rounded-lg border',
        'mdS:grid mdS:grid-cols-[1fr_1px_1fr] mdS:gap-y-6 mdS:py-8 mdS:border-site-accent-500 mdS:bg-gradient-to-l mdS:from-site-neutrals-600 mdS:to-site-neutrals-900',
        "mdS:[grid-template-areas:'title_divider_desc'_'talents_divider_links']",
        'md:border-site-accent-200 md:flex md:gap-y-0 md:bg-none md:py-0',
        className,
      )}
    >
      <p
        id={titleId}
        className={twMerge(
          'site-display-xs text-site-neutrals-0 px-6 pt-6 text-center font-medium',
          'mdS:site-display-s mdS:self-end mdS:px-8 mdS:pt-0 mdS:text-left mdS:font-bold mdS:[grid-area:title]',
          'md:site-display-xs md:self-auto md:px-6 md:pt-6 md:text-center md:font-medium',
        )}
      >
        {title}
      </p>

      <p
        className={twMerge(
          'site-paragraph-s text-site-neutrals-100 px-6 pt-3 text-center',
          'mdS:text-site-neutrals-0 mdS:px-8 mdS:pt-0 mdS:text-left mdS:[grid-area:desc]',
          'md:text-site-neutrals-100 md:px-6 md:pt-3 md:text-center',
        )}
      >
        {description}
      </p>

      <div
        className={twMerge(
          'from-site-neutrals-600 to-site-neutrals-900 flex justify-center bg-gradient-to-t p-6',
          'mdS:justify-start mdS:bg-none mdS:px-8 mdS:py-0 mdS:[grid-area:talents]',
          'md:justify-center md:bg-gradient-to-t md:p-6',
        )}
      >
        <ul className="mdS:max-w-none flex w-full max-w-[16.875rem] flex-col gap-2.5 md:max-w-[16.875rem]">
          {talents.map((talent, index) => (
            <li key={talent.name} className={talentClasses[index]}>
              <div className="bg-site-neutrals-0 flex w-fit items-center gap-2 rounded p-1.5">
                <Image
                  src={talent.image.url}
                  alt=""
                  role="presentation"
                  width={80}
                  height={80}
                  className="size-10 shrink-0 rounded-sm object-cover"
                />
                <div className="flex flex-col pr-1">
                  <span className="flex items-center gap-2.5">
                    <span className="site-paragraph-m text-site-neutrals-600 whitespace-nowrap font-medium">
                      {talent.name}
                    </span>
                    <NextImage
                      src={`${process.env.NEXT_PUBLIC_BASE_PATH ?? ''}${talent.countryFlag.src}`}
                      alt={talent.countryFlag.alt}
                      width={20}
                      height={20}
                      unoptimized
                    />
                  </span>
                  <span className="site-paragraph-xs text-site-neutrals-600 -mt-0.5 whitespace-nowrap">
                    {talent.role}
                  </span>
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>

      <span
        aria-hidden
        className="bg-site-neutrals-600 mdS:block mdS:[grid-area:divider] hidden h-[9.1875rem] w-px self-center md:hidden"
      />

      <ul
        className={twMerge(
          'bg-site-neutrals-800 flex flex-col pb-1',
          'mdS:bg-transparent mdS:px-8 mdS:pb-0 mdS:[grid-area:links]',
          'md:bg-site-neutrals-800 md:px-0 md:pb-1',
        )}
      >
        {links.map((link) => (
          <li key={link.href}>
            <a
              href={link.href}
              className={twMerge(
                'border-site-neutrals-600 site-paragraph-s text-site-neutrals-0 group flex items-center justify-center gap-2.5 border-t px-6 py-2.5',
                'mdS:justify-start mdS:border-t-0 mdS:px-0',
                'md:justify-center md:border-t md:px-6',
              )}
            >
              <span className="group-hover:underline">{link.label}</span>
              <IconComponent iconName="ArrowForward" size="IconSM" className="bg-site-neutrals-200" />
            </a>
          </li>
        ))}
      </ul>
    </section>
  );
}
